import { Suspense, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { GooeyToaster } from "goey-toast";
import "goey-toast/styles.css";
import "react-toastify/dist/ReactToastify.css";
import FeatureNavTW from "./FeatureNavTW";
import BellNavBridge from "./BellNavBridge";
import ToastClearAllButton from "./ToastClearAllButton";
import ErrorBoundary from "./ErrorBoundary";
import { PermissionCheckFailed } from "./RequireCapability";
import { useAuthenticate, useGetOrganizationId } from "../cacheQueries";
import { sessionCheckFailed } from "../auth/session";

export default function AppLayout() {
  // Remount the routed subtree when the active org changes. Pages that fetch
  // via useQuery already refetch via useOrgChangeGuard, but pages that use a
  // bare fetch() inside useEffect (ConfigScanners, ConfigUsers, AccuntSettings)
  // hold their data in useState and would otherwise show the previous org's
  // data forever. Keying <Outlet> forces React to unmount + remount the page,
  // which re-runs every useEffect with the new org context.
  //
  // Wait until the membership list has resolved before mounting the routed
  // page — otherwise the initial cold load would mount once with the org
  // undefined and then remount once more when myOrganizations arrives, which
  // tosses any in-progress map/chart bootstrap for nothing.
  const { data: organizationId } = useGetOrganizationId();
  const location = useLocation();

  // Keep the last resolved org so a *transient* undefined does NOT unmount +
  // remount the routed page (which destroys/rebuilds the page's Mapbox map and
  // re-runs every data fetch — the "flash" + full refetch). The org key churns
  // when the focus/pageshow re-probe of ["authenticate"] briefly changes
  // user?.uid, which changes useGetOrganizationId's key
  // (["myOrganizations", user?.uid]) so its data goes undefined for a tick.
  // A REAL org switch still changes the value and remounts (to clear stale
  // data); only a flap back to the same org is absorbed. The expired-token →
  // /login redirect is a separate effect (main.tsx), so keeping the page
  // mounted here does not affect it.
  const lastOrgRef = useRef<string | number | undefined>(undefined);
  if (organizationId != null) lastOrgRef.current = organizationId;
  const effectiveOrg = organizationId ?? lastOrgRef.current;

  // No routed page mounts before the session and the org have loaded, so when
  // /authenticate itself is failing (a 5xx, the network) the page area would
  // stay blank. Say so instead. useAuthenticate keeps retrying, and the page
  // mounts as soon as it answers.
  const session = useAuthenticate();

  return (
    <div className="absolute top-0 left-0 w-full min-h-screen bg-background dark:bg-dark-background">
      <FeatureNavTW alwaysExpanded={true} />
      {/* Error boundary wraps only the routed page — the sidebar, bell and
          toasts stay alive if a page crashes. `resetKey` (not `key`) clears
          a caught error when the route changes: navigating away from a
          crashed page recovers, but a routed sub-dialog opening/closing
          (e.g. /heatmap/:mac/:start/:stop) no longer remounts the page and
          its map surface. See ErrorBoundary.tsx and resilience-deep-dive.md (W1). */}
      <ErrorBoundary resetKey={location.pathname}>
        <Suspense>
          {effectiveOrg !== undefined ? (
            <Outlet key={effectiveOrg} />
          ) : sessionCheckFailed(session) ? (
            <PermissionCheckFailed />
          ) : null}
        </Suspense>
      </ErrorBoundary>
      <BellNavBridge />
      {/* `!top-24` (~96px) on both toasters clears the h-12 top nav AND the
          ToastClearAllButton pinned below it at top-14, so the pill reads as
          the column's header rather than overlapping the first toast. The
          pill used to sit at top-3, inside the nav, on top of the org
          switcher; moving it down is what pushed this from 56 to 96. Keep
          this in step with the pill's own `top-` and with GooeyToaster's
          `offset` below — three numbers, one column. */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        stacked={true}
        className="!top-24 max-[480px]:!left-2 max-[480px]:!right-2 max-[480px]:!w-auto max-[480px]:!p-0"
        toastClassName="max-[480px]:!rounded-[10px] max-[480px]:!mb-2"
      />
      {/* Drone-detection toasts only — the morphing pill-to-card stack the
          detections use (see showDroneDetectionNotification). Every other
          toast in the app stays on react-toastify above. offset matches the
          toastify column: below the h-12 top bar. */}
      {/* showTimestamp off: the library stamps its fire time as "12.05.20" in
          the corner — cryptic, and the bell card already carries the real
          flight window. */}
      {/* Collapsed peek-stack (expand=false): newer toasts sit on top with
          older edges peeking, up to 10 live at once; past 10 the oldest drops.
          The toast is the interruption — the bell badge is the counter, and
          every detection keeps its bell card regardless of what drops here. */}
      <GooeyToaster
        position="top-right"
        // Matches the toastify column: below the nav and below the Clear-all
        // pill (see the comment above the ToastContainer).
        offset={96}
        closeButton
        showTimestamp={false}
        expand={false}
        gap={8}
        visibleToasts={10}
        maxQueue={10}
        queueOverflow="drop-oldest"
        // Horizontal-only dismissal, like the bell's swipe (mirrored: toasts
        // leave rightward). Requires both local patches: goey forwards the
        // prop to sonner, and sonner's direction lock respects the allowed
        // axes — its stock lock grabbed whichever axis moved first by ±1px,
        // so a thumb's noisy first pixel regularly locked the gesture
        // vertical and the rightward drag went dead.
        swipeDirections={['right']}
      />
      <ToastClearAllButton />
    </div>
  );
}
